import { apiFetch, apiFetchJson } from '@wundo/client/api.ts';
import type { PaginatedResponse } from '@/types/pagination.ts';
import { PAGE_SIZE } from '@/lib/pageSize.ts';

/** One answered NPS survey, flattened for the table. */
export interface NpsResponseRow {
  id: string;
  created_at: string;
  organisation_id: string;
  organisation_name: string;
  cohort_id: string | null;
  /* In a chat, or on the closing form a pilot's last mail links to. */
  source: 'chat' | 'closing_survey';
  user_id: string;
  user_name: string;
  user_email: string | null;
  nps_score: number | null;
  nps_reason: string | null;
  conversation_quality_score: number | null;
  time_saved: string | null;
  time_saved_estimate: string | null;
  open_feedback: string | null;
  /* Questions an organisation added to its own survey, keyed by question. */
  custom_answers: Record<string, unknown>;
}

export interface NpsResponseFilters {
  organisation_id?: string;
  cohort_id?: string;
  broadcast_id?: string;
  date_from?: string;
  date_to?: string;
}

/** A survey that went out, and how many answers came back. */
export interface NpsBroadcast {
  id: string;
  skill_name: string;
  scheduled_for: string;
  response_count: number;
}

function toParams(filters: NpsResponseFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.organisation_id) {
    params.set('organisation_id', filters.organisation_id);
  }
  if (filters.cohort_id) params.set('cohort_id', filters.cohort_id);
  if (filters.broadcast_id) params.set('broadcast_id', filters.broadcast_id);
  if (filters.date_from) params.set('date_from', filters.date_from);
  if (filters.date_to) params.set('date_to', filters.date_to);
  return params;
}

export function fetchNpsBroadcasts(): Promise<NpsBroadcast[]> {
  return apiFetchJson<NpsBroadcast[]>('/nps-responses/broadcasts');
}

export function fetchNpsResponses(
  filters: NpsResponseFilters,
  page = 1,
  limit: number = PAGE_SIZE,
): Promise<PaginatedResponse<NpsResponseRow>> {
  const params = toParams(filters);
  params.set('page', String(page));
  params.set('limit', String(limit));
  return apiFetchJson<PaginatedResponse<NpsResponseRow>>(
    `/nps-responses?${params.toString()}`,
  );
}

/**
 * The filtered list as a file, exported by the backend and saved by the
 * browser.
 *
 * Not a plain link: the endpoint is on the API origin and answers to a session
 * cookie, so the file is fetched like every other request and handed to a
 * throwaway anchor as a blob.
 */
export async function downloadNpsResponsesCsv(
  filters: NpsResponseFilters,
): Promise<void> {
  const query = toParams(filters).toString();
  const response = await apiFetch(`/nps-responses/export${query ? `?${query}` : ''}`);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `nps-responses-${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
